import React, { useEffect, useMemo } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { Card, Table, Tabs } from "antd";
import classNames from "classnames";
import { find, isNil, map } from "lodash";
import { InfoCircleOutlined } from "@ant-design/icons";
import { DropdownInput } from "components";
import useScrollToTop from "hooks/useScrollToTop";
import {
  AICC_LANGUAGE_STATUS,
  AICC_LANGUAGE_STATUS_COLOR,
  AICC_LANGUAGE_STATUS_LABEL,
  AiccLanguageStatus,
} from "constants/aiccLanguage";
import {
  AICC_PROMPT_GROUPS_IN_ORDER,
  AICC_PROMPT_GROUP_LABEL,
  AICC_PROMPT_GROUP_SLUG,
  AiccPromptGroup,
  aiccPromptGroupFromSlug,
} from "constants/aiccPrompt";
import { AiccPromptSummary } from "types/model/aiccPrompt";
import { useLearningAssetsStore } from "../../useLearningAssetsStore";
import { useAiccManagedLanguageNames } from "../../LearningAssets/hooks/useAiccManagedLanguageNames";
import { useAiccPromptsOfGroup } from "../../hooks/useAiccPrompts";
import { useAiccPromptModels } from "../../hooks/useAiccPromptModels";
import { AICC_PROMPT_ROOT_PATH, aiccPromptDetailPath, aiccPromptPath } from "../paths";
import getColumns from "./columnConfig";
import { NO_DRAFT_NOTICE, versionTabLabel } from "./configs";
import assetStyles from "../../LearningAssets/LearningAssets.module.scss";
import styles from "./PromptList.module.scss";

/**
 * 🔴 THE COPIES ARE WHAT EXISTS, NOT A FIXED PAIR. A draft lives only between Create Preview and the
 * next publish, so one tab is the common case; the tabs are rendered from the answer rather than from a
 * list of statuses. The version on the published tab is a label — it is the flow's number and no
 * endpoint offers an arbitrary version of a prompt.
 */
function PromptList() {
  const history = useHistory();
  const { group: slug } = useParams<{ group: string }>();
  const group = aiccPromptGroupFromSlug(slug);

  const { languages, areLanguagesFresh, isLanguagesLoading } = useAiccManagedLanguageNames();
  const languageId = useLearningAssetsStore((state) => state.languageId);
  const setLanguageId = useLearningAssetsStore((state) => state.setLanguageId);
  const activeStatus = useLearningAssetsStore((state) => state.activeStatus);
  const setActiveStatus = useLearningAssetsStore((state) => state.setActiveStatus);
  const { versions, isLanguageDraftOpen, isLoading } = useAiccPromptsOfGroup(languageId, group);
  const { modelsById } = useAiccPromptModels();

  useScrollToTop();

  useEffect(() => {
    if (!areLanguagesFresh || !languages[0] || find(languages, { id: languageId })) {
      return;
    }
    setLanguageId(languages[0].id);
  }, [areLanguagesFresh, languageId, languages, setLanguageId]);

  const selectedVersion = find(versions, { status: activeStatus }) ?? versions[0];
  const openedStatus = selectedVersion?.status;
  /**
   * 🔴 THE TAB DECIDES, exactly as it does on the twelve other asset screens (`useLearningAssetVersions`):
   * the published copy is frozen and every other copy is the working one. The row's own `editable` field
   * says the same thing when it is right and the wrong thing when it is not, so it is not consulted — and
   * the label here has to agree with what the detail screen will do, or a row reads "Configure" and opens
   * something read-only.
   */
  const isEditable = !!openedStatus && openedStatus !== AICC_LANGUAGE_STATUS.public;

  const columns = useMemo(
    () =>
      getColumns({
        modelsById,
        isEditable,
        onConfigure: ({ key }: AiccPromptSummary) =>
          group && history.push(aiccPromptDetailPath(group, key, openedStatus)),
      }),
    [group, history, isEditable, modelsById, openedStatus]
  );

  if (!group) {
    return <Redirect to={`${AICC_PROMPT_ROOT_PATH}/${AICC_PROMPT_GROUP_SLUG[AICC_PROMPT_GROUPS_IN_ORDER[0]]}`} />;
  }

  return (
    <Card title="Prompt" className="page-container">
      <Tabs
        className={styles.moduleTabs}
        activeKey={group}
        onChange={(key) => history.push(aiccPromptPath(key as AiccPromptGroup))}
      >
        {map(AICC_PROMPT_GROUPS_IN_ORDER, (tab) => (
          <Tabs.TabPane key={tab} tab={AICC_PROMPT_GROUP_LABEL[tab]} />
        ))}
      </Tabs>

      <div className={assetStyles.toolbar}>
        <div className={assetStyles.filters}>
          <div className={assetStyles.filter}>
            <span className={assetStyles.filterLabel}>Languages:</span>
            <div className={assetStyles.filterSelect}>
              <DropdownInput
                options={map(languages, ({ id, name }) => ({ label: name, value: id }))}
                value={languageId}
                onChange={setLanguageId}
                placeholder="Language"
                mode="single"
                disabled={isLanguagesLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {!isLoading && !isNil(languageId) && !isLanguageDraftOpen && (
        <div className={styles.readOnlyNotice}>
          <InfoCircleOutlined />
          {NO_DRAFT_NOTICE}
        </div>
      )}

      <Tabs
        className={assetStyles.statusTabs}
        style={
          {
            "--status-tab-active-color": selectedVersion
              ? AICC_LANGUAGE_STATUS_COLOR[selectedVersion.status]
              : undefined,
          } as React.CSSProperties
        }
        activeKey={selectedVersion?.status}
        onChange={(key) => setActiveStatus(key as AiccLanguageStatus)}
      >
        {map(versions, (version) => (
          <Tabs.TabPane
            key={version.status}
            tab={
              <span
                className={assetStyles.statusTabLabel}
                style={{ "--status-tab-color": AICC_LANGUAGE_STATUS_COLOR[version.status] } as React.CSSProperties}
              >
                {versionTabLabel(AICC_LANGUAGE_STATUS_LABEL[version.status], version.version)}
              </span>
            }
          />
        ))}
      </Tabs>

      <Table<AiccPromptSummary>
        className={classNames(assetStyles.table, styles.table)}
        scroll={{ x: "100%" }}
        loading={isLoading || isNil(languageId)}
        dataSource={selectedVersion?.prompts}
        columns={columns}
        rowKey={(record) => `${selectedVersion?.status}-${record.key}`}
        pagination={false}
        onRow={(record) => ({
          className: styles.clickableRow,
          onClick: () => history.push(aiccPromptDetailPath(group, record.key, openedStatus)),
        })}
      />
    </Card>
  );
}

export default PromptList;
